/**
 * DashboardModeContext — single source of truth for the dual-role
 * dashboard mode toggle.
 *
 * Modes:
 *   - 'hosting' → provider dashboard + hosting-focused sidebar
 *   - 'renting' → renter dashboard + renting-focused sidebar
 *
 * Mode selection rules (in order):
 *   1. Explicit user choice persisted in localStorage is respected,
 *      stored under a per-user key so different accounts on the same
 *      browser don't bleed state into each other.
 *   2. No stored preference for THIS user → 'hosting' if they have
 *      listings, 'renting' otherwise.
 *
 * The per-user storage key (B2 fix, 2026-04-27) prevents cross-session
 * contamination — e.g., test account A picks 'renting', logs out,
 * test account B (with listings) logs in and previously inherited
 * the 'renting' choice. With per-user keys, account B has no stored
 * preference and correctly defaults to 'hosting'.
 *
 * The legacy global key `merrily:dashboardMode` is no longer read or
 * written. Existing entries become orphaned (localStorage cleans up
 * naturally; not worth a one-time migration for what's effectively
 * dev-only test data).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

export const DASHBOARD_MODE_STORAGE_KEY_PREFIX = 'merrily:dashboardMode';
const VALID_MODES = ['hosting', 'renting'];

const storageKey = userId => `${DASHBOARD_MODE_STORAGE_KEY_PREFIX}:${userId || 'anon'}`;

const defaultContextValue = {
  currentMode: 'renting',
  setMode: () => {},
  hasListings: false,
  canSwitchModes: false,
};

const DashboardModeContext = createContext(defaultContextValue);

const readStoredMode = userId => {
  if (typeof window === 'undefined') return null; // SSR
  try {
    const v = window.localStorage.getItem(storageKey(userId));
    return VALID_MODES.includes(v) ? v : null;
  } catch (e) {
    // localStorage can throw in private mode / when disabled — treat as
    // no preference rather than surfacing an error.
    return null;
  }
};

const writeStoredMode = (userId, mode) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(userId), mode);
  } catch (e) {
    // Swallow quota / disabled-storage errors — the in-memory state is
    // still correct for this session.
  }
};

/**
 * Pure reducer for "given storage + hasListings, what mode should we be
 * in?" Separated so it's easy to unit-test and reason about.
 *
 * Respects the user's stored choice unconditionally. A user who picks
 * 'hosting' with zero listings stays in 'hosting' — they see the
 * empty host dashboard with a "Create your first listing" CTA, which
 * IS the natural next step. Auto-flipping them back to 'renting'
 * (the previous behavior) hid the host onboarding path entirely:
 * first-time signups have zero listings by definition, so they never
 * stayed in hosting long enough to see the listing CTA.
 *
 * Default (no stored preference): 'hosting' if listings exist, 'renting'
 * otherwise. The default is only material for users who haven't made
 * an explicit choice yet.
 */
export const computeDesiredMode = (stored, hasListings) => {
  if (VALID_MODES.includes(stored)) return stored;
  return hasListings ? 'hosting' : 'renting';
};

/**
 * Wraps the app subtree with mode state. Must sit INSIDE the Redux
 * <Provider> since we derive hasListings from state.user.
 */
export const DashboardModeProvider = ({ children }) => {
  const hasListings = useSelector(state => !!state.user?.currentUserHasListings);
  // Per-user namespacing — see the file-level comment. `currentUserId`
  // is undefined during auth load and during SSR. Reads under the
  // 'anon' bucket then; once the real userId resolves the effect below
  // re-evaluates and switches buckets.
  const currentUserId = useSelector(state => state.user?.currentUser?.id?.uuid);
  // Stripe-connected = a real signal of host intent even before a
  // first listing is published. Source of truth is the connect-account
  // duck (populated by fetchCurrentUser with the stripeAccount include).
  const stripeConnected = useSelector(
    state => !!state.stripeConnectAccount?.stripeAccount?.id
  );

  // SSR-safe initial state: compute mode from hasListings ONLY, never
  // read localStorage during useState init. Reading storage in the
  // initializer produces different first renders on server (null) vs.
  // client (possibly a stored pref) — that hydration mismatch broke the
  // whole app on earlier Task 4 attempts. The stored pref is applied by
  // the reconcile effect below, post-mount.
  const [currentMode, setCurrentModeState] = useState(() =>
    computeDesiredMode(null, hasListings)
  );

  // Re-evaluate when EITHER hasListings loads asynchronously OR when
  // the userId changes (login / logout / account switch). The latter
  // is the B2 case — a different user's stored preference must not
  // bleed in from a previous session.
  useEffect(() => {
    const desired = computeDesiredMode(readStoredMode(currentUserId), hasListings);
    setCurrentModeState(prev => (prev === desired ? prev : desired));
  }, [hasListings, currentUserId]);

  const setMode = useCallback(
    mode => {
      if (!VALID_MODES.includes(mode)) return;
      // No-op guard: avoid a redundant state update (and re-render) when
      // the mode is already set — RouteModeSync may call this on every
      // route change.
      setCurrentModeState(prev => (prev === mode ? prev : mode));
      writeStoredMode(currentUserId, mode);
    },
    [currentUserId]
  );

  return (
    <DashboardModeContext.Provider
      value={{
        currentMode,
        setMode,
        hasListings,
        // Toggle is visible to anyone who is hosting OR clearly intends
        // to host (Stripe connected). Previously gated only on
        // hasListings, which hid the toggle from first-time signups
        // mid-onboarding; was then over-corrected to `true` (always
        // visible), which surfaced the toggle to pure renters with no
        // host intent. Stripe-connected catches the in-between case:
        // user finished payout setup but hasn't published yet.
        canSwitchModes: hasListings || stripeConnected,
      }}
    >
      {children}
    </DashboardModeContext.Provider>
  );
};

/**
 * Hook — read the current mode + setter + canSwitchModes flag.
 */
export const useDashboardMode = () => useContext(DashboardModeContext);

// ─── Route-mode auto-sync ─────────────────────────────────────────────
// Dual-role users landing on a mode-specific URL get flipped into that
// mode so sidebar + topbar stay consistent with the page content. This
// covers the navigation paths the explicit controls don't: bookmarks,
// browser back/forward, and deep links (e.g. a booking-request email
// linking straight to /account/bookings while the user is in renting
// mode).
//
// Explicit controls (ModeToggle, DashboardSidebar) both setMode AND
// navigate to a matching URL, so by the time this effect runs after a
// toggle, pathname and mode already agree and it no-ops. The
// `implied === currentMode` check is the loop guard: setMode →
// currentMode updates → effect re-fires → implied equals currentMode →
// skip. No feedback loop.
//
// Prefix match so /account/bookings/:id inherits from /account/bookings.
// Pure renters (canSwitchModes === false) skip entirely.

const HOSTING_PATH_PREFIXES = [
  '/account/host-dashboard',
  '/account/bookings',
  '/account/calendar',
  '/listings',
];
const RENTING_PATH_PREFIXES = ['/account/rentals', '/account/favorites'];

/**
 * The route name the Dashboard nav link should point to for a given mode.
 * Pointing at the side that matches currentMode means the destination URL
 * already agrees with the mode, so RouteModeSync no-ops on arrival — no
 * mode flip, no flicker. (Same principle ModeToggle/DashboardSidebar use.)
 *
 * dashboardRole is an SSR / pre-hydrate fallback for when currentMode
 * isn't resolved yet. Note 'HostDashboardPage' (/account/host-dashboard)
 * is the real host route; 'ProviderDashboardPage' just redirects to renter.
 */
export const dashboardPageForMode = (currentMode, dashboardRole) => {
  if (currentMode === 'hosting') return 'HostDashboardPage';
  if (currentMode === 'renting') return 'RenterDashboardPage';
  return dashboardRole === 'provider' ? 'HostDashboardPage' : 'RenterDashboardPage';
};

export const pathImpliesMode = pathname => {
  if (!pathname) return null;
  for (const p of HOSTING_PATH_PREFIXES) {
    if (pathname === p || pathname.startsWith(p + '/')) return 'hosting';
  }
  for (const p of RENTING_PATH_PREFIXES) {
    if (pathname === p || pathname.startsWith(p + '/')) return 'renting';
  }
  return null;
};

/**
 * Renders nothing. Mount inside the Router so useLocation resolves.
 * Only calls setMode when the pathname implies a mode AND that mode
 * differs from currentMode. SSR-safe: the effect never runs on the
 * server.
 */
export const RouteModeSync = () => {
  const { currentMode, setMode, canSwitchModes } = useDashboardMode();
  const location = useLocation();
  const pathname = location?.pathname;

  useEffect(() => {
    if (!canSwitchModes) return;
    const implied = pathImpliesMode(pathname);
    if (!implied) return;
    if (implied === currentMode) return;
    setMode(implied);
  }, [pathname, canSwitchModes, currentMode, setMode]);

  return null;
};
